import ViewInArIcon from "@mui/icons-material/ViewInAr";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ExportButton from "Common/Components/Buttons/ExportButton";
import ColumnVisibilityDropdown from "Common/Components/ColumnFilteringPopUp/ColumnFilteringPopUp";
import { DynamicMenuItem } from "Common/Components/DynamicActionMenu/DynamicActionMenu";
import FormattedDate from "Common/Components/NameWithDate.tsx/FormattedDate";
import NameWithDate from "Common/Components/NameWithDate.tsx/NameWithDate";
import SearchBox from "Common/Components/SearchBox/SearchBox";
import StatusBadge from "Common/Components/StatusBadge/StatusBadge";
import MasterTableContainer from "Common/Tables/MasterFiltersTable/MasterTableContainer";
import LoadingSpinner from "components/LoadingSpinner";
import { TableKeys } from "Constants/TableKeys/TableKeys";
import usePreservedTableState from "CustomHooks/usePreservedTableState";
import { app_modules } from "helpers/apps_modules_list";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import usePermission from "hooks/usePermission";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { createSelector } from "reselect";
import {
  exportIssuePackingCSV,
  fetchWarehousePacking,
  handleRevertIssueForPacking,
} from "slices/Warehouse/Packing/IssuePacking/thunk";
import { resetIssuePackingState } from "slices/Warehouse/Packing/IssuePacking/reducer";
import RevertPaking from "../../Revert/RevertPacking";
import Tooltip from "@mui/material/Tooltip";
import UndoIcon from "@mui/icons-material/Undo";
import { showConfirm, showError, showSuccess } from "helpers/swalHelper";
import { SweetAlertResult } from "sweetalert2";
import StatusChip from "pages/Enquiry/Components/StatusChip";
import Chip from "components/Chips/Chip";

interface IssuePackingProps {
  warehouseId: any;
}

const IssuePacking = ({ warehouseId }: IssuePackingProps) => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const selectPRs = createSelector(
    (state: any) => state.WarehouseIssuePackingReducer,
    (data) => ({
      list: data.list,
      rowCount: data.rowCount,
      loading: data.loading,
      error: data.error,
    }),
  );

  const { list, rowCount, loading, error } = useSelector(selectPRs);

  const { canView, canCreate, canUpdate, canDelete } = usePermission(
    app_modules?.warehouse?.app_name,
    app_modules?.warehouse?.modules?.packing,
  );

  // 🔍 If opened from /tags-search/:tags, use tag from URL as effective search
  const { tags: tagsParam } = useParams<{ tags?: string }>();
  const tagFromUrl = useMemo(() => {
    if (!tagsParam) return "";
    try {
      return decodeURIComponent(tagsParam).trim();
    } catch {
      return tagsParam.trim();
    }
  }, [tagsParam]);
  const isTagSearch = Boolean(tagFromUrl);

  const tableState = usePreservedTableState({
    key: TableKeys.WAREHOUSE.PACKING.ISSUE_PACKING,
    defaults: {
      pageIndex: 0,
      pageSize: 10,
      search: "",
      searchInput: "",
      sorting: [],
      columnFilters: [],
      filterValue: [],
    },
  });

  const {
    pageIndex,
    pageSize,
    search,
    searchInput,
    sorting,
    columnFilters,
    filterValue,
    setPageIndex,
    setPageSize,
    setSearch,
    setSearchInput,
    setSorting,
    setColumnFilters,
    setFilterValue,
    resetState,
  } = tableState;

  // const [pageIndex, setPageIndex] = useState(0);
  // const [pageSize, setPageSize] = useState(10);
  // const [columnFilters, setColumnFilters] = useState<any>([]);
  // const [filterValue, setFilterValue] = useState<any[]>([]);
  // const [sorting, setSorting] = useState<SortingState>([]);
  // const [searchInput, setSearchInput] = useState("");
  // const [search, setSearch] = useState("");
  const [data, setData] = useState([]);
  const [show, setShow] = useState<boolean>(false);
  const [editPackage, setEditPackage] = useState<boolean>(false);
  const [tableInstance, setTableInstance] = useState<any>();

  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";

  // When on tags-search route, always use URL tag as the search term
  const effectiveSearch = tagFromUrl || search;

  // Sync URL tag into search box once when present
  useEffect(() => {
    if (!tagFromUrl) return;
    setSearchInput(tagFromUrl);
    setSearch(tagFromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tagFromUrl]);

  useEffect(() => {
    if (isTagSearch || warehouseId) {
      dispatch(
        fetchWarehousePacking({
          page: pageIndex + 1,
          pageSize,
          sort: sortField,
          order: sortField ? sortDirection : "",
          search: effectiveSearch,
          ...(isTagSearch ? { type: "tag" } : {}),
          filters: filterValue,
          ...(!isTagSearch ? { warehouse_id: warehouseId } : {}),
        }),
      );
    } else {
      dispatch(resetIssuePackingState());
    }
  }, [
    dispatch,
    pageIndex,
    pageSize,
    sorting,
    effectiveSearch,
    filterValue,
    warehouseId,
    isTagSearch,
  ]);

  // Handle search input with debounce
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput]);

  useEffect(() => {
    setData(list);
  }, [list]);

  const handleSearchChange = useCallback((e: any) => {
    setSearchInput(e.target.value);
  }, []);

  const handleExport = useCallback(async () => {
    if (!warehouseId && !isTagSearch) {
      throw new Error("Please select a warehouse");
    }

    try {
      const result = await exportIssuePackingCSV({
        ...(!isTagSearch ? { warehouse_id: warehouseId } : {}),
        search: effectiveSearch,
        ...(isTagSearch ? { type: "tag" } : {}),
        filters: filterValue,
      });

      return result;
    } catch (error: any) {
      console.error("Export error:", error);
      throw error;
    }
  }, [warehouseId, effectiveSearch, filterValue, isTagSearch]);

  const handleRevert = (sales_order_product_id: string) => {
    showConfirm(
      "Are you sure you want to revert this packing? This action cannot be undone.",
    ).then((result: SweetAlertResult) => {
      if (result.isConfirmed) {
        dispatch(handleRevertIssueForPacking(sales_order_product_id))
          .unwrap()
          .then(() => {
            showSuccess("Packing reverted successfully");
            dispatch(
              fetchWarehousePacking({
                page: pageIndex + 1,
                pageSize,
                sort: sortField,
                order: sortField ? sortDirection : "",
                search: effectiveSearch,
                ...(isTagSearch ? { type: "tag" } : {}),
                filters: filterValue,
                ...(!isTagSearch ? { warehouse_id: warehouseId } : {}),
              }),
            );
          })
          .catch((error: any) => {
            console.error("Failed to revert packing:", error);
            showError(error?.message || error || "Failed to revert packing");
          });
      }
    });
  };

  const DynamicAction =
    canView || canCreate
      ? [
          {
            header: "Action",
            enableColumnFilter: false,
            enableSorting: false,
            cell: ({ row }: { row: any }) => {
              const data = row.original;
              console.log("data", data);
              const menuItems = [
                {
                  icon: <VisibilityIcon fontSize="small" color="info" />,
                  label: "View Details",
                  permission: canView,
                  onClick: (item: any) => {
                    navigate(
                      `/warehouse-packing-view-details/${item?.sales_order_product_id}`,
                      {
                        state: { data: item, listType: "Transfer" },
                      },
                    );
                  },
                  color: "primary",
                },
                // ✅ spread conditional item
                ...(data?.packing_status?.toLowerCase() !== "packed"
                  ? [
                      {
                        icon: ViewInArIcon,
                        label: "Create Packing",
                        permission: canCreate,
                        link: (item: any) =>
                          `/warehouse-create-packing/${item?.sales_order_product_id}`,
                        color: "primary",
                      },
                    ]
                  : []),

                // ✅ Revert only if packing_status is 'issued for packing'
                ...(row.original?.packing_status?.toLowerCase() ===
                "issued for packing"
                  ? [
                      {
                        label: "Revert",
                        icon: <UndoIcon fontSize="small" color="error" />,
                        permission: canUpdate, // or a separate canRevert if you have
                        onClick: () =>
                          handleRevert(row.original?.sales_order_product_id),
                      },
                    ]
                  : []),
              ];
              return <DynamicMenuItem row={data} menuItems={menuItems} />;
            },
          },
        ]
      : [];

  const columns = useMemo(() => {
    const columns = [
      // {
      //   header: "Sr No.",
      //   accessorKey: "sr_no",
      //   enableColumnFilter: false,
      //   enableSorting: false,
      //   cell: ({ row, table }: { row: any; table: any }) => {
      //     const pageIndex = table.getState().pagination.pageIndex;
      //     const pageSize = table.getState().pagination.pageSize;
      //     const rowIndex = row.index;
      //     return pageIndex * pageSize + rowIndex + 1;
      //   },
      // },

      {
        header: "Sales Order No.",
        accessorKey: "sales_order_number",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT?.WAREHOUSE?.PACKING?.FILTER_DROPDOWN(
          "sales_order_number",
          warehouseId,
        )}`,
      },
      {
        header: "Company Name",
        accessorKey: "customer_name",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT?.WAREHOUSE?.PACKING?.FILTER_DROPDOWN(
          "customer_name",
          warehouseId,
        )}`,
        cell: ({ row }: { row: any }) => {
          return row.original.customer_name || "-";
        },
      },
      {
        header: "SO Date",
        accessorKey: "sales_order_date",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => (
          <FormattedDate date={row.original.sales_order_date} />
        ),
      },
      // {
      //   header: "Product Name",
      //   accessorKey: "item_name",
      //   enableColumnFilter: true,
      //   enableSorting: true,
      //   filterType: "dropdown" as const,
      //   filterFetchUrl: `/packing/issue-packing/dropdown/?field=item_name`,
      //   cell: ({ row }: { row: any }) => {
      //     return row.original.item_name || "-";
      //   },
      // },
      // {
      //   header: "Ordered Qty",
      //   accessorKey: "ordered_quantity",
      //   enableColumnFilter: true,
      //   enableSorting: true,
      //   filterType: "number" as const,
      //   cell: ({ row }: { row: any }) => {
      //     return row.original.ordered_quantity || "-";
      //   },
      // },
      // {
      //   header: "Packed Qty",
      //   accessorKey: "packed_quantity",
      //   enableColumnFilter: true,
      //   enableSorting: true,
      //   filterType: "number" as const,
      //   cell: ({ row }: { row: any }) => {
      //     return row.original.packed_quantity || "-";
      //   },
      // },
      // {
      //   header: "Balance Qty",
      //   accessorKey: "balance_quantity",
      //   enableColumnFilter: true,
      //   enableSorting: true,
      //   filterType: "number" as const,
      //   cell: ({ row }: { row: any }) => {
      //     return row.original.balance_quantity || "-";
      //   },
      // },
      {
        header: "Status",
        accessorKey: "packing_status",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT?.WAREHOUSE?.PACKING?.FILTER_DROPDOWN(
          "packing_status",
          warehouseId,
        )}`,
        cell: ({ row }: { row: any }) => {
          const status = row.original.packing_status;
          return <StatusChip status={row.original.packing_status} />;

          const getStatusColor = (status: string) => {
            switch (status?.toLowerCase()) {
              case "partially packed":
                return "#fde4b4";
              case "packed":
                return "#0CBA91";
              default:
                return "#e5ccff";
            }
          };

          const displayText = status
            ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
            : "Pending Packing";

          return (
            <StatusBadge
              status={displayText}
              bgColor={status ? getStatusColor(status) : "#fde4b4"}
              color="#000000ff"
            />
          );
        },
      },
      {
        header: "Notes",
        accessorKey: "notes",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const notes = row.original?.notes;
          if (!notes) return "-";

          return (
            <Tooltip
              placement="top"
              title={notes}
              componentsProps={{
                tooltip: {
                  sx: {
                    whiteSpace: "pre-line",
                    maxWidth: 350,
                    fontSize: "13px",
                  },
                },
              }}
            >
              <div
                style={{
                  minWidth: "150px",
                  maxWidth: "250px",
                  display: "-webkit-box",
                  WebkitLineClamp: 4,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  overflowWrap: "anywhere",
                  cursor: "pointer",
                }}
              >
                {notes}
              </div>
            </Tooltip>
          );
        },
      },
       {
        header: "Tags",
        accessorKey: "tags",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
                  const tags: string[] = row.original.tags || [];
                  if (!tags.length) return "-";
        
                  const maxVisible = 3;
                  const visible = tags.slice(0, maxVisible);
                  const remaining = tags.length - visible.length;
                  const tooltipText = tags.join(", ");
        
                  return (
                    <Tooltip
                      arrow
                      placement="top"
                      title={tooltipText}
                      componentsProps={{
                        tooltip: {
                          sx: {
                            maxWidth: 300,
                            fontSize: "13px",
                            whiteSpace: "normal",
                          },
                        },
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "4px",
                          minWidth: "150px",
                          cursor: "pointer",
                        }}
                      >
                        {visible.map((tag: string) => (
                          <Chip key={tag}>{tag}</Chip>
                        ))}
                        {remaining > 0 && <Chip>+{remaining} more</Chip>}
                      </div>
                    </Tooltip>
                  );
                },
      },
      {
        header: "Created By/Date",
        accessorKey: "created_at",
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => (
          <NameWithDate
            name={row.original.created_by_user.username}
            date={row.original.created_at}
          />
        ),
      },
      ...DynamicAction,
    ];

    return [...columns];
  }, [navigate, canView, canCreate, warehouseId]);

  return (
    <div
      className="d-flex flex-column"
      style={{
        flex: "1 1 auto",
        minHeight: 0,
        maxHeight: "100%",
        overflow: "hidden",
      }}
    >
      <div className="flex-shrink-0">
        <Row className="align-items-center mecord-search-row g-2">
          <SearchBox
            value={searchInput}
            onChange={handleSearchChange}
            placeholder="Search..."
          />

          <Col
            xs={12}
            md={12}
            lg={8}
            xl={7}
            className="d-flex justify-content-end align-items-center gap-2"
          >
            {tableInstance && (
              <ColumnVisibilityDropdown
                columns={tableInstance.getAllLeafColumns()}
                excludedColumns={["action", "sr_no"]}
              />
            )}
            {canView && <ExportButton onExport={handleExport} />}
          </Col>
        </Row>
      </div>

      <div
        style={{
          flex: "1 1 auto",
          minHeight: 0,
          maxHeight: "100%",
          marginTop: 4,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {loading ? (
          <LoadingSpinner />
        ) : error ? (
          <div className="text-center py-4 text-danger">
            <h5>Error: {error}</h5>
          </div>
        ) : (
          <MasterTableContainer
            columns={columns || []}
            data={data}
            totalRecords={rowCount}
            pageIndex={pageIndex}
            pageSize={pageSize}
            onPageChange={setPageIndex}
            onPageSizeChange={setPageSize}
            columnFilters={columnFilters}
            setColumnFilters={setColumnFilters}
            colValue={setFilterValue}
            sorting={sorting}
            onSortingChange={setSorting}
            isBordered={false}
            isPagination={true}
            isGlobalFilter={false}
            SearchPlaceholder="Search ..."
            manualSorting={true}
            onTableReady={setTableInstance}
          />
        )}
      </div>
      <RevertPaking show={show} setShow={setShow} />
    </div>
  );
};

export default IssuePacking;
